import { useState, useEffect, useCallback, createContext } from 'react';
import {
  signIn,
  signOut,
  confirmSignIn,
  getCurrentUser,
  fetchAuthSession,
  type SignInOutput,
} from 'aws-amplify/auth';
import {
  permissionsForRoles,
  primaryRole,
  type Permission,
} from '../lib/permissions';

interface User {
  email: string;
  userId: string;
  roles: string[];
  roleLabel: string;
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  error: string | null;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<SignInOutput>;
  completeNewPassword: (newPassword: string) => Promise<SignInOutput>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
  hasPermission: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth(): AuthContextType {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
  });

  const checkAuth = useCallback(async () => {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      const rawGroups = session.tokens?.idToken?.payload['cognito:groups'];
      const roles = Array.isArray(rawGroups)
        ? (rawGroups as string[])
        : rawGroups
          ? [String(rawGroups)]
          : [];
      setState({
        isAuthenticated: true,
        isLoading: false,
        user: {
          email: user.signInDetails?.loginId ?? '',
          userId: user.userId,
          roles,
          roleLabel: primaryRole(roles),
        },
        error: null,
      });
    } catch {
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
      });
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email: string, password: string): Promise<SignInOutput> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      // Clear any stale Amplify sign-in state before attempting login
      try {
        await signOut();
      } catch {
        // Ignore - no existing session to clear
      }
      const result = await signIn({
        username: email,
        password,
        options: { authFlowType: 'USER_PASSWORD_AUTH' },
      });
      // A freshly-invited user comes back with isSignedIn=false and a
      // CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED next step — don't treat that
      // as signed in; let the caller collect a new password (completeNewPassword).
      if (result.isSignedIn) {
        await checkAuth();
      } else {
        setState((prev) => ({ ...prev, isLoading: false }));
      }
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  };

  const logout = async () => {
    await signOut();
    setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error: null,
    });
  };

  const completeNewPassword = async (newPassword: string): Promise<SignInOutput> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const result = await confirmSignIn({ challengeResponse: newPassword });
      if (result.isSignedIn) {
        await checkAuth();
      } else {
        setState((prev) => ({ ...prev, isLoading: false }));
      }
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Could not set password';
      setState((prev) => ({ ...prev, isLoading: false, error: errorMessage }));
      throw error;
    }
  };

  const getAccessToken = async (): Promise<string> => {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() ?? '';
  };

  const hasPermission = useCallback(
    (permission: Permission): boolean =>
      permissionsForRoles(state.user?.roles ?? []).has(permission),
    [state.user],
  );

  return { ...state, login, completeNewPassword, logout, getAccessToken, hasPermission };
}

export { AuthContext };
